// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRoles } from '../services/roleService';
import { parseScopes } from './useRoleCatalog';
import { useSharedSession } from '../auth/useSharedSession';
import { toUserRole } from '../../helios/services/authService';
import type { UserRole } from '../../helios/types';

/** Scope types a role can be granted at from Admin Center. */
export type AssignableScope = 'ORGANIZATION' | 'REGION' | 'SITE';

const ASSIGNABLE_SCOPES: AssignableScope[] = ['ORGANIZATION', 'REGION', 'SITE'];

export interface AssignableRole {
  roleId: string;
  /** Raw IAM role name, e.g. "OrgAdmin". */
  roleName: string;
  /** The Admin Center role this maps to, for its label; null for any other role. */
  canonicalRole: UserRole | null;
  description: string | null;
  /** Where this role may be granted, in hierarchy order. Never empty. */
  scopes: AssignableScope[];
}

export interface AssignableRolesView {
  roles: AssignableRole[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Every role `GET /api/v1/roles` returns that can be granted at an
 * organization, region or site, with the scopes it allows (AB#35260). Unlike
 * `useRoleCatalog`, nothing is narrowed to the four canonical roles: IAM's
 * catalog decides what can be granted, and IAM decides on submit whether
 * the caller may grant it.
 */
export function useAssignableRoles(): AssignableRolesView {
  const { organizationId, isLoading: sessionLoading } = useSharedSession();

  // Same key as useRoleCatalog: one fetch serves both.
  const rolesQuery = useQuery({
    queryKey: ['admin-center', 'roles', organizationId],
    queryFn: () => getRoles(),
    enabled: !!organizationId,
  });

  const roles = useMemo(
    (): AssignableRole[] =>
      (rolesQuery.data ?? [])
        .map(r => {
          const declared = parseScopes(r.applicableScopes);
          return {
            roleId: r.roleId,
            roleName: r.roleName,
            canonicalRole: toUserRole(r.roleName),
            description: r.description,
            scopes: ASSIGNABLE_SCOPES.filter(scope => declared.includes(scope)),
          };
        })
        .filter(role => role.scopes.length > 0),
    [rolesQuery.data]
  );

  return {
    roles,
    isLoading: sessionLoading || (!!organizationId && rolesQuery.isPending),
    isError: rolesQuery.isError,
  };
}
